import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
// import { getRegionByCode, getCountryByCode, buildDownloadDataNew } from '@intelligence/utils/commonUtils';
import { buildDownloadDataNew } from '@intelligence/utils/commonUtils';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';

/**
 * 根据品类数据源和genre_dimension获取group_by配置
 * @param genreSource 品类数据源 ('sensortower' | 'appmagic')
 * @param genreDimension 用户选择的品类维度
 */
const getGenreKeys = (genreSource: string, genreDimension: string) => {
  if (genreSource === GENRE_SOURCE_MAP.SENSORTOWER) {
    // Sensor Tower 数据源
    switch (genreDimension) {
      case 'main_genre':
        return ['sensortower_genre'];
      case 'sub_genre':
        return ['sensortower_genre', 'sensortower_sub_genre'];
      default:
        return ['sensortower_genre', 'sensortower_sub_genre'];
    }
  } else {
    // App Magic 数据源
    switch (genreDimension) {
      case 'super_genre':
        return ['super_genre'];
      case 'major_category':
        return ['super_genre', 'major_category'];
      case 'mid_category':
        return ['super_genre', 'major_category', 'mid_category'];
      case 'sub_category':
        return ['super_genre', 'major_category', 'mid_category', 'sub_category'];
      default:
        return ['super_genre', 'major_category'];
    }
  }
};

const formatList = <T extends IGenreMarketProfile>(list?: T[] | null): T[] => {
  if (isEmpty(list)) {
    return [];
  }

  return list!;
};

export const getCasualGenreProfileConfig = (data: any, params: any, allParams: any) => {
  const granularity = params?.granularity || 'monthly';
  // const regionOptions = allParams?.regionOptions || [];
  const isGroupByDate = allParams?.[DOWNLOAD_CFG_KEY.DATE_METHOD] !== DataCalculation.Aggregated;
  const isGroupByRegion = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;
  // 是否展示%
  const isShowRatio = allParams?.ratio_show === 1;
  // 需要下载的指标映射
  const metricMap: IRecord<boolean> =
    params?.metrics?.reduce((acc: IRecord<boolean>, cur: string) => {
      return { ...acc, [cur]: true };
    }, {}) ?? {};

  const ext = data?.ext || {};
  const marketInfo = ext?.market_info || {};
  const regionNameENMap = marketInfo?.['intel_region -> en'] || {};
  const regionNameCNMap = marketInfo?.['intel_region -> cn'] || {};
  const marketNameENMap = marketInfo?.['market -> en'] || {};
  const marketNameCNMap = marketInfo?.['market -> cn'] || {};
  const regionCodeMap = marketInfo?.['market -> region'] || {};

  const genreTitleMap: IRecord<string> = {
    sensortower_genre: 'Sensor Tower Genre',
    sensortower_sub_genre: 'Sensor Tower Sub Genre',
    super_genre: 'Super Genre',
    major_category: 'Level-1 Genre',
    mid_category: 'Level-2 Genre',
    sub_category: 'Level-3 Genre',
  };
  const genreKeys = getGenreKeys(allParams?.genreSource, allParams?.genre_dimension);
  const genreConfig = genreKeys.map((genreKey) => {
    return {
      title: genreTitleMap[genreKey] || genreKey,
      key: genreKey,
    };
  });

  const regionCountryConfig = [
    {
      title: 'Region_English',
      // key: (item: any) => getRegionByCode(item?.region, regionOptions)?.area_name_en || '',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = (item?.region || '').toLowerCase();
          return regionNameENMap?.[itemRegionCode] || itemRegionCode;
        }

        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameENMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Region_Chinese',
      // key: (item: any) => getRegionByCode(item?.region, regionOptions)?.area_name_ch || '',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = (item?.region || '').toLowerCase();
          return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
        }

        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Country_English',
      // key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_en || '',
      key: (item: any) => {
        const itemMarketCode = (item?.market || '').toLowerCase();
        return marketNameENMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
    {
      title: 'Country_Chinese',
      // key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_ch || '',
      key: (item: any) => {
        const itemMarketCode = (item?.market || '').toLowerCase();
        return marketNameCNMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
  ];

  const commonSheetConfig = [
    ...genreConfig,
    {
      title: 'Date',
      key: (item: any) => {
        const itemDate = item?.date || '';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
      when: () => isGroupByDate,
    },
    ...regionCountryConfig,
    {
      title: 'Sensor Tower Downloads',
      key: (item: any) => item?.sensortower__download || '',
      when: () => metricMap?.sensortower__download,
    },
    {
      title: '%',
      key: (item: any) => item?.sensortower__download_ratio || '',
      when: () => isShowRatio && metricMap?.sensortower__download,
    },
    {
      title: 'Sensor Tower DAU',
      key: (item: any) => item?.sensortower__dau || '',
      when: () => metricMap?.sensortower__dau,
    },
    {
      title: '%',
      key: (item: any) => item?.sensortower__dau_ratio || '',
      when: () => isShowRatio && metricMap?.sensortower__dau,
    },
    {
      title: 'Sensor Tower MAU',
      key: (item: any) => item?.sensortower__mau || '',
      when: () => metricMap?.sensortower__mau,
    },
    {
      title: '%',
      key: (item: any) => item?.sensortower__mau_ratio || '',
      when: () => isShowRatio && metricMap?.sensortower__mau,
    },
    {
      title: 'Sensor Tower Revenue',
      key: (item: any) => item?.sensortower__revenue || '',
      when: () => metricMap?.sensortower__revenue,
    },
    {
      title: '%',
      key: (item: any) => item?.sensortower__revenue_ratio || '',
      when: () => isShowRatio && metricMap?.sensortower__revenue,
    },
  ];

  return {
    stat: {
      sheetName: 'Casual GenreProfile',
      style: 'normal',
      data: buildDownloadDataNew(commonSheetConfig, formatList(data?.list)),
    },
  };
};
